import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Upload, Zap, FileText, Sparkles, ArrowRight, Download, Smartphone, CheckCircle, Star, ChevronDown, Clock, Globe, LayoutGrid, TrendingUp, Lightbulb, Focus, Play } from 'lucide-react';
import Lottie from 'lottie-react';
import Layout from '../components/layout/Layout';
import { useAuthModal } from '../contexts/AuthModalContext';
import toast from 'react-hot-toast';
import axios from 'axios';

const HomePageV2: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { openAuthModal } = useAuthModal();
  const [freeCreditsAmount, setFreeCreditsAmount] = useState<number>(10);
  const [activeMobileStep, setActiveMobileStep] = useState<number>(0);
  const [activeDesktopStep, setActiveDesktopStep] = useState<number>(0);
  const [activeWebStep, setActiveWebStep] = useState<number>(0);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(0);
  const [mobileAnimations, setMobileAnimations] = useState<any[]>([null, null, null]);

  // Fetch Lottie animations (mobile only)
  useEffect(() => {
    const loadAnimations = async () => {
      try {
        const mobileAnims = await Promise.all([
          fetch('/images/screenshots/mobile/step-1-upload.json').then(res => res.json()),
          fetch('/images/screenshots/mobile/step-2-process.json').then(res => res.json()),
          fetch('/images/screenshots/mobile/step-3-download.json').then(res => res.json())
        ]);
        setMobileAnimations(mobileAnims);
      } catch (error) {
        console.error('Failed to load Lottie animations:', error);
      }
    };
    loadAnimations();
  }, []);

  // Fetch free credits amount
  useEffect(() => {
    const fetchFreeCredits = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'https://api.metapic.app';
        const response = await axios.get(`${apiUrl}/api/v1/site-settings/public`);
        if (response.data.success && response.data.data?.free_credits_amount) {
          const amount = parseInt(response.data.data.free_credits_amount);
          if (!isNaN(amount)) {
            setFreeCreditsAmount(amount);
          }
        }
      } catch (err) {
        console.error('Failed to fetch free credits:', err);
      }
    };
    fetchFreeCredits();
  }, []);

  useEffect(() => {
    if (searchParams.get('auth') === 'required') {
      openAuthModal();
      searchParams.delete('auth');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, openAuthModal]);

  const features = [
    {
      title: 'AI-Powered Keywords',
      description: 'Generate SEO-optimized keywords that rank on Shutterstock, Adobe Stock, and Getty Images.',
      icon: Sparkles
    },
    {
      title: 'Batch Processing',
      description: 'Process hundreds of images at once. Save hours of manual metadata writing.',
      icon: Upload
    },
    {
      title: 'Multi-Platform Export',
      description: 'Export to CSV, JSON, or directly to your favorite stock platforms.',
      icon: FileText
    },
    {
      title: 'Microstock AI Tools',
      description: 'A complete AI images workflow for microstock submission. Prompt, generate, upscale, all in one place.',
      icon: Zap
    }
  ];

  const stats = [
    { label: 'Images Processed', value: '10M+' },
    { label: 'Active Contributors', value: '50K+' },
    { label: 'Time Saved', value: '2M+ hrs' },
    { label: 'Platforms Supported', value: '15+' }
  ];

  const faqs = [
    {
      question: 'Do you keep my images on your server?',
      answer: (
        <div className="space-y-3 text-gray-600">
          <p>No. Your images are downscaled just for AI recognition. Once processing is done, we delete them from the server.</p>
          <p><strong>Web App:</strong> We keep the smallest size thumbnails only for your gallery view.</p>
          <p><strong>Want 100% no trace?</strong> Use our desktop app or mobile app where processing happens locally on your device.</p>
        </div>
      )
    },
    {
      question: 'Is there a free trial?',
      answer: (
        <div className="space-y-3 text-gray-600">
          <p>Yes! Get started with {freeCreditsAmount} free credits (no credit card required):</p>
          <ul className="list-disc list-inside space-y-2">
            <li>Process {freeCreditsAmount} images for free</li>
            <li>See results before committing to a plan</li>
          </ul>
        </div>
      )
    },
    {
      question: 'Can I export metadata to microstock platforms?',
      answer: (
        <div className="space-y-3 text-gray-600">
          <p>Yes! Export your metadata in multiple formats:</p>
          <ul className="list-disc list-inside space-y-2">
            <li><strong>CSV format</strong> - Compatible with Shutterstock, Adobe Stock, Getty Images</li>
            <li><strong>Copy to clipboard</strong> - Quick paste to any platform</li>
            <li><strong>Bulk edit</strong> - Edit multiple files before export</li>
          </ul>
          <p>All metadata includes SEO-optimized titles, descriptions, and 50+ relevant keywords per image.</p>
        </div>
      )
    },
    {
      question: 'How many files can I process at once?',
      answer: (
        <div className="space-y-3 text-gray-600">
          <ul className="list-disc list-inside space-y-2">
            <li><strong>Web version:</strong> Current limit at 100 files (1,000 per upload coming soon)</li>
            <li><strong>Desktop app:</strong> Unlimited high-res files</li>
            <li><strong>Mobile app:</strong> Batch processing on your device</li>
          </ul>
        </div>
      )
    }
  ];

  const mobileSteps = [
    {
      number: 1,
      title: 'Take a picture or use your old picture',
      description: 'Capture new stock photos on the go or select from your gallery.',
      image: 'mobile-step-1' // Placeholder - replace with actual image path
    },
    {
      number: 2,
      title: 'Generate Metadata in batch',
      description: 'AI automatically creates titles, descriptions, and keywords for all your images.',
      image: 'mobile-step-2'
    },
    {
      number: 3,
      title: 'Upload directly to microstock',
      description: 'Submit to Shutterstock, Adobe Stock, and more right from your phone.',
      image: 'mobile-step-3'
    }
  ];

  const desktopSteps = [
    {
      number: 1,
      title: 'Import photos in bulk',
      description: 'Drag and drop hundreds of images at once from your computer.',
      image: 'desktop-step-1'
    },
    {
      number: 2,
      title: 'AI generates metadata',
      description: 'Advanced AI analyzes each image and creates professional metadata.',
      image: 'desktop-step-2'
    },
    {
      number: 3,
      title: 'Export and upload',
      description: 'Download CSV files or use direct integration with stock platforms.',
      image: 'desktop-step-3'
    }
  ];

  const webSteps = [
    {
      number: 1,
      title: 'Smart batch processing',
      description: 'Process multiple images simultaneously with cloud AI.',
      image: 'web-step-2'
    },
    {
      number: 2,
      title: 'Download & manage',
      description: 'Manage all your metadata in one central dashboard.',
      image: 'web-step-3'
    },
    {
      number: 3,
      title: 'Powerful CSV editor',
      description: 'Edit your existing CSV and even re-generate without need of image.',
      image: 'web-step-1'
    }
  ];

  return (
    <Layout>
      <main className="bg-white">
        
        {/* Hero Section - Stripe Style with Gradient */}
        <section className="relative overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50"></div>
          
          {/* Grid Pattern Overlay */}
          <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-28 pb-12 md:pb-32">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
              
              {/* Left Column - Content (7 cols) */}
              <div className="lg:col-span-7 space-y-6 md:space-y-8 relative z-10 lg:pr-48">
                
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-gray-200 text-sm font-medium text-gray-700">
                  <Star className="w-3.5 h-3.5 text-indigo-600 fill-current" />
                  Made for a faster microstock workflow

                </div>

                {/* Headline */}
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.1] tracking-tight">
                  Create Stock Ready Metadata{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
                    Anywhere
                  </span>
                </h1>

                {/* Mobile Image - After heading */}
                <div className="lg:hidden py-4">
                  <div className="relative w-full max-w-lg mx-auto">
                    <img 
                      src="/images/Hero-mobile-app-preview.webp" 
                      alt="Metapic mobile app preview"
                      className="w-full h-auto object-contain drop-shadow-2xl"
                      loading="eager"
                    />
                  </div>
                </div>

                {/* Description */}
                <p className="text-lg sm:text-xl md:text-2xl text-gray-600 leading-relaxed max-w-2xl">
                 Generate titles, descriptions, and keywords in seconds. Use Metapic anywhere! from your phone, laptop, or browser.

                </p>

                {/* Platform Apps */}
                <div className="flex flex-wrap gap-3 pt-4 md:pt-8">
                  <a 
                    href="/desktop"
                    onClick={() => {
                      if (typeof window !== 'undefined' && (window as any).fbq) {
                        (window as any).fbq('track', 'ViewContent', { content_name: 'Desktop App Page' });
                      }
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:border-gray-300 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Desktop
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.metapic.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      if (typeof window !== 'undefined' && (window as any).fbq) {
                        (window as any).fbq('track', 'Lead', { content_name: 'Google Play Download' });
                        (window as any).fbq('trackCustom', 'PlayStoreClick', { platform: 'Android' });
                      }
                    }}
                    className="inline-flex items-center justify-center hover:opacity-90 transition-opacity"
                  >
                    <img 
                      src="/badges/google-play-badge.svg" 
                      alt="Get it on Google Play"
                      className="h-10"
                    />
                  </a>
                </div>

                {/* CTA Link */}
                <div className="pt-4 md:pt-8">
                  <a 
                    href="/upload"
                    onClick={() => {
                      if (typeof window !== 'undefined' && (window as any).fbq) {
                        (window as any).fbq('track', 'ViewContent', { content_name: 'Get Started - Upload' });
                      }
                    }}
                    className="inline-flex items-center gap-2 text-base md:text-lg font-semibold text-gray-900 hover:text-indigo-600 transition-colors group"
                  >
                    Get started for free
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
                
                {/* Getting Started Link */}
                <div className="pt-2">
                  <a 
                    href="/getting-started" 
                    className="inline-flex items-center text-sm text-gray-600 hover:text-indigo-600 transition-colors group"
                  >
                    <Play className="w-4 h-4 mr-1.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                    <span>New to microstock? Watch our complete free course</span>
                  </a>
                </div>
              </div>

              {/* Right Column - Spacer for Grid */}
              <div className="lg:col-span-5 relative">
                {/* Spacer - actual image positioned absolutely */}
              </div>

            </div>
          </div>

          {/* Hero Image - Absolutely positioned to overflow on the right */}
          <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-[55%] pointer-events-none">
            <div className="relative h-full flex items-center justify-end pr-0">
              <img 
                src="/images/Hero-mobile-app-preview.webp" 
                alt="Metapic mobile app preview"
                className="h-[90%] w-auto object-contain"
                style={{ transform: 'translateX(10%)' }}
              />
            </div>
          </div>
        </section>

        {/* Benefits Section - Hidden on mobile */}
        <section className="hidden md:block py-12 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Section Header */}
            <header className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
                Faster and Smoother workflow
              </h2>
              <p className="text-base md:text-lg text-gray-600">
                Spend less time typing metadata and more time creating.
              </p>
            </header>

            {/* Benefits Grid - 2 columns on mobile, 3 on desktop */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">

              {/* Benefit 1 */}
              <div className="p-4 md:p-6 bg-white rounded-lg md:rounded-xl border border-gray-200 hover:border-indigo-300 transition-all duration-200 hover:shadow-md">
                <div className="flex w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg items-center justify-center mb-2 md:mb-3">
                  <Clock className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">Save your time!</h3>
                <p className="hidden md:block text-sm text-gray-600">Spend less time typing metadata and more time creating. Perfect for daily contributors.</p>
              </div>

              {/* Benefit 2 */}
              <div className="p-4 md:p-6 bg-white rounded-lg md:rounded-xl border border-gray-200 hover:border-indigo-300 transition-all duration-200 hover:shadow-md">
                <div className="flex w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg items-center justify-center mb-2 md:mb-3">
                  <Globe className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">Submit from anywhere</h3>
                <p className="hidden md:block text-sm text-gray-600">Upload to multiple microstock platforms in one click. No switching tabs or repeating tasks.</p>
              </div>

              {/* Benefit 3 */}
              <div className="p-4 md:p-6 bg-white rounded-lg md:rounded-xl border border-gray-200 hover:border-indigo-300 transition-all duration-200 hover:shadow-md">
                <div className="flex w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg items-center justify-center mb-2 md:mb-3">
                  <LayoutGrid className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">Work the way you want</h3>
                <p className="hidden md:block text-sm text-gray-600">Edit one photo or manage hundreds at once. Everything feels effortless.</p>
              </div>

              {/* Benefit 4 */}
              <div className="p-4 md:p-6 bg-white rounded-lg md:rounded-xl border border-gray-200 hover:border-indigo-300 transition-all duration-200 hover:shadow-md">
                <div className="flex w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg items-center justify-center mb-2 md:mb-3">
                  <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">Boost your acceptance rate</h3>
                <p className="hidden md:block text-sm text-gray-600">High resolution exports and proper metadata help your images perform better on agencies.</p>
              </div>

              {/* Benefit 5 */}
              <div className="p-4 md:p-6 bg-white rounded-lg md:rounded-xl border border-gray-200 hover:border-indigo-300 transition-all duration-200 hover:shadow-md">
                <div className="flex w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg items-center justify-center mb-2 md:mb-3">
                  <Lightbulb className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">Never run out of ideas</h3>
                <p className="hidden md:block text-sm text-gray-600">AI prompt generator and bulk tools keep you creative with zero limitations.</p>
              </div>

              {/* Benefit 6 */}
              <div className="p-4 md:p-6 bg-white rounded-lg md:rounded-xl border border-gray-200 hover:border-indigo-300 transition-all duration-200 hover:shadow-md">
                <div className="flex w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg items-center justify-center mb-2 md:mb-3">
                  <Focus className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">Focus on creating, not the busywork</h3>
                <p className="hidden md:block text-sm text-gray-600">Let Metapic handle the repetitive tasks so you can stay productive.</p>
              </div>

            </div>
          </div>
        </section>

        {/* How It Works - Mobile App */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            
            {/* Section Header */}
            <header className="text-center max-w-3xl mx-auto mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-xs font-medium text-indigo-700 mb-4">
                <Smartphone className="w-3.5 h-3.5" />
                Mobile App
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                How it works on{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                  mobile
                </span>
              </h2>
              <p className="text-lg text-gray-600">
                Generate professional, SEO-ready metadata for stock photos and videos — anywhere, anytime, right from your phone.
              </p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              
              {/* Steps - Left Side */}
              <div className="space-y-4">
                {mobileSteps.map((step, index) => (
                  <div
                    key={index}
                    onClick={() => setActiveMobileStep(index)}
                    className={`group cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                      activeMobileStep === index
                        ? 'border-indigo-600 bg-indigo-50/50 shadow-lg shadow-indigo-100'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex gap-3">
                      {/* Step Number */}
                      <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-base transition-colors ${
                        activeMobileStep === index
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                      }`}>
                        {step.number}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1">
                        <h3 className={`text-base font-semibold mb-1.5 transition-colors ${
                          activeMobileStep === index ? 'text-indigo-900' : 'text-gray-900'
                        }`}>
                          {step.title}
                        </h3>
                        <p className="hidden md:block text-sm text-gray-600 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.metapic.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      if (typeof window !== 'undefined' && (window as any).fbq) {
                        (window as any).fbq('track', 'Lead', { content_name: 'Mobile Section - Google Play Download' });
                        (window as any).fbq('trackCustom', 'PlayStoreClick', { platform: 'Android', section: 'Mobile How It Works' });
                      }
                    }}
                    className="inline-flex items-center justify-center hover:opacity-90 transition-opacity"
                  >
                    <img 
                      src="/badges/google-play-badge.svg" 
                      alt="Get it on Google Play"
                      className="h-10"
                    />
                  </a>
                  <a 
                    href="/mobile-app"
                    className="inline-flex items-center justify-center px-6 h-10 bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200 rounded-lg font-semibold text-sm transition-colors"
                  >
                    Learn More
                  </a>
                </div>
              </div>

              {/* Mobile Phone Mockup - Right Side */}
              <div className="relative flex justify-center lg:justify-end">
                <div className="relative w-80">
                  {/* Phone Frame */}
                  <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl shadow-gray-900/30 border-[8px] border-white bg-white">
                    
                    {/* Screen - Full screen with 1:2 aspect ratio */}
                    <div className="aspect-[1/2] bg-white relative overflow-hidden">
                      {/* Only render active Lottie animation */}
                      <div className="w-full h-full">
                        {mobileAnimations[activeMobileStep] ? (
                          <Lottie
                            key={`lottie-${activeMobileStep}`}
                            animationData={mobileAnimations[activeMobileStep]}
                            loop={true}
                            autoplay={true}
                            style={{ width: '100%', height: '100%' }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
                            <div className="animate-pulse text-indigo-600">
                              <Smartphone className="w-8 h-8" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Decorative Elements */}
                  <div className="absolute -top-6 -right-6 w-24 h-24 bg-indigo-400/20 rounded-full blur-3xl -z-10"></div>
                  <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-purple-400/20 rounded-full blur-3xl -z-10"></div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* How It Works - Desktop App */}
        <section className="py-16 md:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            
            {/* Section Header */}
            <header className="text-center max-w-3xl mx-auto mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 border border-purple-100 text-xs font-medium text-purple-700 mb-4">
                <Download className="w-3.5 h-3.5" />
                Desktop App
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Powerful batch processing on{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                  desktop
                </span>
              </h2>
              <p className="text-lg text-gray-600">
                Process hundreds of images with advanced AI metadata generation. Available for Mac and Windows
              </p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 lg:gap-12 items-center">
              
              {/* Desktop Mockup - Left Side (7 cols) */}
              <div className="order-2 lg:order-1 lg:col-span-7 relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-gray-900/20 border border-gray-300 bg-gray-100">
                  {/* Browser Bar */}
                  <div className="bg-gray-200 px-4 py-3 flex items-center gap-2 border-b border-gray-300">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    </div>
                  </div>
                  
                  {/* Screen Content */}
                  <div className="aspect-[16/10] bg-white relative overflow-hidden">
                    {/* Desktop screenshots */}
                    {[1, 2, 3].map((step, index) => (
                      <div
                        key={index}
                        className={`absolute inset-0 transition-opacity duration-500 ${
                          activeDesktopStep === index ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        <img
                          src={`/images/screenshots/desktop/step${step}-desktop.jpg`}
                          alt={`Desktop step ${step}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-8 -left-8 w-32 h-32 bg-purple-400/20 rounded-full blur-3xl -z-10"></div>
                <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-pink-400/20 rounded-full blur-3xl -z-10"></div>
              </div>

              {/* Steps - Right Side (3 cols) */}
              <div className="order-1 lg:order-2 lg:col-span-3 space-y-3">
                {desktopSteps.map((step, index) => (
                  <div
                    key={index}
                    onClick={() => setActiveDesktopStep(index)}
                    className={`group cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                      activeDesktopStep === index
                        ? 'border-purple-600 bg-purple-50/50 shadow-lg shadow-purple-100'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex gap-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-base transition-colors ${
                        activeDesktopStep === index
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                      }`}>
                        {step.number}
                      </div>
                      
                      <div className="flex-1">
                        <h3 className={`text-base font-semibold mb-1.5 transition-colors ${
                          activeDesktopStep === index ? 'text-purple-900' : 'text-gray-900'
                        }`}>
                          {step.title}
                        </h3>
                        <p className="hidden md:block text-sm text-gray-600 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <a 
                    href="/desktop"
                    onClick={() => {
                      if (typeof window !== 'undefined' && (window as any).fbq) {
                        (window as any).fbq('track', 'ViewContent', { content_name: 'Desktop Section - Download' });
                      }
                    }}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium text-sm transition-all duration-200"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </a>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* How It Works - Web App */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            
            {/* Section Header */}
            <header className="text-center max-w-3xl mx-auto mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-50 border border-pink-100 text-xs font-medium text-pink-700 mb-4">
                <Zap className="w-3.5 h-3.5" />
                Web App
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Work from anywhere with{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-orange-600">
                  web app
                </span>
              </h2>
              <p className="text-lg text-gray-600">
                No installation needed — just login and start generating metadata
              </p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 lg:gap-12 items-center">
              
              {/* Steps - Left Side (3 cols) */}
              <div className="lg:col-span-3 space-y-3">
                {webSteps.map((step, index) => (
                  <div
                    key={index}
                    onClick={() => setActiveWebStep(index)}
                    className={`group cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                      activeWebStep === index
                        ? 'border-pink-600 bg-pink-50/50 shadow-lg shadow-pink-100'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex gap-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-base transition-colors ${
                        activeWebStep === index
                          ? 'bg-pink-600 text-white'
                          : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                      }`}>
                        {step.number}
                      </div>
                      
                      <div className="flex-1">
                        <h3 className={`text-base font-semibold mb-1.5 transition-colors ${
                          activeWebStep === index ? 'text-pink-900' : 'text-gray-900'
                        }`}>
                          {step.title}
                        </h3>
                        <p className="hidden md:block text-sm text-gray-600 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <a 
                    href="/upload"
                    onClick={() => {
                      if (typeof window !== 'undefined' && (window as any).fbq) {
                        (window as any).fbq('track', 'ViewContent', { content_name: 'Web Section - Get Started' });
                      }
                    }}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium text-sm transition-all duration-200"
                  >
                    Get started
                    <ArrowRight className="w-4 h-4" />
                  </a>
                  <a 
                    href="/features"
                    onClick={() => {
                      if (typeof window !== 'undefined' && (window as any).fbq) {
                        (window as any).fbq('track', 'ViewContent', { content_name: 'Features Page' });
                      }
                    }}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white hover:bg-gray-50 text-gray-900 border border-gray-300 rounded-lg font-medium text-sm transition-all duration-200"
                  >
                    Learn more
                  </a>
                </div>
              </div>

              {/* Browser Mockup - Right Side (7 cols) */}
              <div className="lg:col-span-7 relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-gray-900/20 border border-gray-300 bg-gray-100">
                  {/* Browser Bar */}
                  <div className="bg-gray-200 px-4 py-3 border-b border-gray-300">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      </div>
                      <div className="flex-1 bg-white rounded-lg px-3 py-1.5 text-xs text-gray-500">
                        metapic.app
                      </div>
                    </div>
                  </div>
                  
                  {/* Screen Content */}
                  <div className="aspect-[16/10] bg-white relative overflow-hidden">
                    {/* Web browser screenshots */}
                    {[1, 2, 3].map((step, index) => (
                      <div
                        key={index}
                        className={`absolute inset-0 transition-opacity duration-500 ${
                          activeWebStep === index ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        <img
                          src={`/images/screenshots/web/step${step}-browser.jpg`}
                          alt={`Web browser step ${step}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-pink-400/20 rounded-full blur-3xl -z-10"></div>
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-orange-400/20 rounded-full blur-3xl -z-10"></div>
              </div>

            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-24 md:py-32 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            
            {/* Section Header */}
            <header className="text-center max-w-3xl mx-auto mb-20">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Everything you need to{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                  scale faster
                </span>
              </h2>
              <p className="text-xl text-gray-600">
                Professional metadata AI generation tools built for a faster and easier workflow.
              </p>
            </header>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div 
                    key={index}
                    className="group relative p-8 bg-white rounded-2xl border border-gray-200 hover:border-gray-300 transition-all duration-200 hover:shadow-lg hover:shadow-gray-900/5"
                  >
                    {/* Icon */}
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-200">
                      <Icon className="w-6 h-6 text-indigo-600" />
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-24 md:py-32 overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600"></div>
          
          {/* Pattern Overlay */}
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>

          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Start generating metadata today
            </h2>
            <p className="text-xl text-indigo-100 mb-10 max-w-2xl mx-auto">
              Create without limits. Submit your microstock anywhere, anytime, your workflow, your rules.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/login"
                onClick={() => {
                  if (typeof window !== 'undefined' && (window as any).fbq) {
                    (window as any).fbq('track', 'Lead', { content_name: 'CTA - Get Free Credits' });
                  }
                }}
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-gray-50 text-gray-900 rounded-xl font-semibold text-lg transition-all duration-200 shadow-xl"
              >
                Get {freeCreditsAmount} free credits
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-indigo-100">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm">No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm">Cancel anytime</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm">24/7 support</span>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 md:py-32 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            
            {/* Section Header */}
            <header className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Frequently Asked{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                  Questions
                </span>
              </h2>
              <p className="text-xl text-gray-600">
                Everything you need to know about Metapic
              </p>
            </header>

            {/* FAQ Accordion */}
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-xl overflow-hidden transition-all duration-300 hover:border-gray-300"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors text-left"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 pr-8">
                      {faq.question}
                    </h3>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-300 ${
                        expandedFaq === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Answer */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      expandedFaq === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="px-6 py-5 bg-gray-50 border-t border-gray-200">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* More Help CTA */}
            <div className="mt-16 p-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl border border-indigo-200 text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Can't find what you're looking for?
              </h3>
              <p className="text-gray-600 mb-6">
                Our support team is here to help. Reach out anytime.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium transition-colors"
              >
                Contact Support
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

      </main>
    </Layout>
  );
};

export default HomePageV2;
